/**
 * Miniature dei file caricati, prima dell'anonimizzazione.
 *
 * PDF: la prima pagina, disegnata nel browser con pdf.js (lo stesso
 * dell'editor) in un canvas piccolo. Immagini: il file stesso, tramite un
 * URL temporaneo da revocare quando il file esce dall'elenco. DOCX, PPTX e
 * CSV: niente miniatura, perche' nel browser non c'e' niente che li
 * disegni (il motore li converte in PDF solo dopo il caricamento); la
 * scheda mostra il tipo del file.
 *
 * Dipendenze iniettabili: pdf.js e il canvas non girano sotto jsdom.
 */
import { loadPdfJs as loadPdfJsVero, loadPdfDocumentFromBytes as loadPdfDocumentFromBytesVero } from "./pdfjsLoader";

export const LARGHEZZA_MINIATURA = 240;

/** "pdf", "immagine" o l'estensione (docx, pptx, csv...). */
export function tipoFile(nome) {
  const parti = String(nome || "").toLowerCase().split(".");
  const est = parti.length > 1 ? parti.pop() : "";
  if (est === "pdf") return "pdf";
  if (["png", "jpg", "jpeg"].includes(est)) return "immagine";
  return est || "file";
}

/**
 * @returns {Promise<{tipo: string, url: string|null, revocabile: boolean, pagine: number|null}>}
 *   url: dati o URL temporaneo da mostrare in un <img>; revocabile: vero
 *   se va passato a revocaMiniatura quando non serve piu'; pagine: quante ne
 *   ha il PDF (1 per un'immagine), null se nel browser non si sa
 */
export async function generaMiniatura(file, deps = {}) {
  const tipo = tipoFile(file?.name);
  if (tipo === "immagine") {
    const crea = deps.createObjectURL || ((f) => URL.createObjectURL(f));
    return { tipo, url: crea(file), revocabile: true, pagine: 1 };
  }
  if (tipo !== "pdf") return { tipo, url: null, revocabile: false, pagine: null };

  const loadPdfJs = deps.loadPdfJs || loadPdfJsVero;
  const loadPdfDocumentFromBytes = deps.loadPdfDocumentFromBytes || loadPdfDocumentFromBytesVero;
  const creaCanvas = deps.creaCanvas || (() => document.createElement("canvas"));
  const bytes = await file.arrayBuffer();
  const pdfjs = await loadPdfJs();
  const doc = await loadPdfDocumentFromBytes(pdfjs, bytes);
  try {
    const pagina = await doc.getPage(1);
    const base = pagina.getViewport({ scale: 1 });
    const vp = pagina.getViewport({ scale: LARGHEZZA_MINIATURA / Math.max(1, base.width) });
    const canvas = creaCanvas();
    canvas.width = Math.ceil(vp.width);
    canvas.height = Math.ceil(vp.height);
    const ctx = canvas.getContext("2d");
    await pagina.render({ canvasContext: ctx, viewport: vp }).promise;
    return { tipo, url: canvas.toDataURL("image/jpeg", 0.82), revocabile: false, pagine: doc.numPages || null };
  } finally {
    try { await doc.destroy?.(); } catch (_) { /* gia' chiuso */ }
  }
}

export function revocaMiniatura(m, revoca = (u) => URL.revokeObjectURL(u)) {
  if (m?.revocabile && m.url) {
    try { revoca(m.url); } catch (_) { /* gia' revocato */ }
  }
}
